import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AuthenticationDetails, CognitoUser, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { cognitoConfig } from '../../config/auth';
import { DEFAULT_BUSINESS_ID, setBusinessId, clearBusinessId } from '../../services/auth';
import { setAuthToken } from '../../services/api';

type AuthContextValue = {
  isAuthenticated: boolean;
  isConfigured: boolean;
  isLoading: boolean;
  authError: string | null;
  userEmail: string | null;
  idToken: string | null;
  login: (username: string, password: string) => Promise<CognitoUserSession>;
  logout: () => void;
  clearError: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const pool =
  cognitoConfig.userPoolId && cognitoConfig.clientId
    ? new CognitoUserPool({
        UserPoolId: cognitoConfig.userPoolId,
        ClientId: cognitoConfig.clientId,
      })
    : null;

const useSyncSession = (session: CognitoUserSession | null) => {
  useEffect(() => {
    if (!session) {
      setAuthToken(null, null);
      clearBusinessId();
      return;
    }

    const token = session.getIdToken()?.getJwtToken() || null;
    setAuthToken(token, DEFAULT_BUSINESS_ID);
    setBusinessId(DEFAULT_BUSINESS_ID);
  }, [session]);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<CognitoUserSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useSyncSession(session);

  useEffect(() => {
    if (!pool) {
      setIsLoading(false);
      return;
    }

    const currentUser = pool.getCurrentUser();
    if (!currentUser) {
      setIsLoading(false);
      return;
    }

    currentUser.getSession((err: Error | null, cognitoSession: CognitoUserSession | null) => {
      if (err || !cognitoSession?.isValid()) {
        setSession(null);
        setIsLoading(false);
        return;
      }

      setSession(cognitoSession);
      setIsLoading(false);
    });
  }, []);

  const login = (username: string, password: string) => {
    return new Promise<CognitoUserSession>((resolve, reject) => {
      if (!pool) {
        const message =
          'Cognito is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID for your Potentia-prod user pool.';
        setAuthError(message);
        setIsLoading(false);
        reject(new Error(message));
        return;
      }

      setAuthError(null);
      setIsLoading(true);

      const user = new CognitoUser({ Username: username, Pool: pool });
      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      user.authenticateUser(authDetails, {
        onSuccess: (result) => {
          setSession(result);
          setIsLoading(false);
          resolve(result);
        },
        onFailure: (err) => {
          const message = err?.message || 'Login failed. Please check your credentials.';
          setAuthError(message);
          setIsLoading(false);
          reject(new Error(message));
        },
        newPasswordRequired: () => {
          const message = 'Password update is required for this user. Complete the reset in Cognito and try again.';
          setAuthError(message);
          setIsLoading(false);
          reject(new Error(message));
        },
      });
    });
  };

  const logout = () => {
    pool?.getCurrentUser()?.signOut();
    setSession(null);
    setAuthToken(null, null);
    clearBusinessId();
  };

  const value = useMemo<AuthContextValue>(() => {
    const idToken = session?.getIdToken()?.getJwtToken() || null;
    const email = (session?.getIdToken()?.payload?.email as string) || null;

    return {
      isAuthenticated: Boolean(session),
      isConfigured: Boolean(pool),
      isLoading,
      authError,
      userEmail: email,
      idToken,
      login,
      logout,
      clearError: () => setAuthError(null),
    };
  }, [session, isLoading, authError, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
